import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { explainQuestion, getAttemptDetails } from '../lib/api';
import { SUBJECTS, formatDate, getPercentageColor, getPercentageBadge } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  CheckCircle, 
  XCircle, 
  ArrowLeft, 
  RotateCcw, 
  Home,
  Loader2,
  Trophy,
  Target,
  Sparkles,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const AI_EXPLANATIONS_ENABLED = process.env.REACT_APP_ENABLE_AI_EXPLANATIONS !== 'false';

const ResultPage = () => {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!location.state?.result);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [selectedExplanation, setSelectedExplanation] = useState(null);
  const [explanations, setExplanations] = useState({});
  const [explanationLoading, setExplanationLoading] = useState({});

  useEffect(() => {
    if (!result) {
      loadResult();
    }
  }, [attemptId]);

  const loadResult = async () => {
    try {
      const response = await getAttemptDetails(attemptId);
      setResult(response.data);
    } catch (err) {
      toast.error('Erro ao carregar resultado');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-500">Carregando resultado...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!result) return null;

  const subjectInfo = SUBJECTS[result.subject];
  const hasAnswerKeyRevisions = result.has_answer_key_revisions;
  const originalScore = result.original_score ?? result.score;
  const originalPercentage = result.original_percentage ?? result.percentage;
  const revisedScore = result.revised_score ?? result.score;
  const revisedPercentage = result.revised_percentage ?? result.percentage;
  const passed = revisedPercentage >= 70;

  const handleAskAgent = async (detail) => {
    const cached = explanations[detail.question_id];
    setSelectedExplanation({
      questionId: detail.question_id,
      questionText: detail.question_text,
      data: cached || null,
    });
    setAiDialogOpen(true);

    if (cached) return;

    setExplanationLoading((prev) => ({ ...prev, [detail.question_id]: true }));

    try {
      const response = await explainQuestion({
        question_id: detail.question_id,
        subject: result.subject,
        subject_name: result.subject_name || subjectInfo?.name,
        question_text: detail.question_text,
        options: detail.options,
        selected_option: detail.selected_option,
        correct_option: detail.correct_option,
        is_correct: detail.is_correct,
      });

      const explanationData = response.data;
      setExplanations((prev) => ({
        ...prev,
        [detail.question_id]: explanationData,
      }));
      setSelectedExplanation({
        questionId: detail.question_id,
        questionText: detail.question_text,
        data: explanationData,
      });
    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao pedir explicação ao Agente V.O.A.R.';
      toast.error(message);
      setAiDialogOpen(false);
    } finally {
      setExplanationLoading((prev) => ({ ...prev, [detail.question_id]: false }));
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fadeIn">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/history')}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 mb-4 transition-colors"
            data-testid="back-to-history"
          >
            <ArrowLeft className="w-4 h-4" />
            Ver histórico
          </button>
        </div>

        {/* Result Card */}
        <div className={`
          rounded-xl p-5 sm:p-8 mb-8 text-center
          ${passed ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-red-50 border-2 border-red-200'}
        `}>
          <div className={`
            w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4
            ${passed ? 'bg-emerald-100' : 'bg-red-100'}
          `}>
            {passed ? (
              <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-600" />
            ) : (
              <Target className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" />
            )}
          </div>

          <h1 className={`text-2xl md:text-4xl font-black mb-2 ${passed ? 'text-emerald-800' : 'text-red-800'}`}>
            {passed ? 'Parabéns!' : 'Continue Estudando!'}
          </h1>
          
          <p className={`text-base sm:text-lg mb-6 ${passed ? 'text-emerald-600' : 'text-red-600'}`}>
            {passed 
              ? 'Você atingiu a pontuação mínima para aprovação!'
              : 'Você precisa de 70% para ser aprovado. Não desista!'
            }
          </p>

          <div className="grid grid-cols-1 sm:flex sm:items-center sm:justify-center gap-5 sm:gap-8">
            <div className="min-w-0">
              <p className={`text-4xl sm:text-5xl font-black ${getPercentageColor(revisedPercentage)}`}>
                {revisedPercentage.toFixed(1)}%
              </p>
              <p className="text-sm text-zinc-500 mt-1">Aproveitamento</p>
            </div>
            <div className="hidden sm:block h-16 w-px bg-zinc-300" />
            <div className="sm:hidden h-px w-full max-w-48 mx-auto bg-zinc-300" />
            <div className="min-w-0">
              <p className="text-4xl sm:text-5xl font-black text-zinc-900">
                {revisedScore}/{result.total_questions}
              </p>
              <p className="text-sm text-zinc-500 mt-1">Acertos</p>
            </div>
          </div>
        </div>

        {hasAnswerKeyRevisions && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="font-bold text-amber-900">
                  Resultado atualizado após revisão
                </h2>
                <p className="text-sm text-amber-900 mt-1">
                  Uma ou mais questões deste simulado tiveram o gabarito revisado ou foram anuladas depois da prova.
                  Por transparência, mantemos a pontuação original e mostramos a pontuação revisada.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  <div className="bg-white/70 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">Resultado original</p>
                    <p className="text-xl font-bold text-amber-950">
                      {originalScore}/{result.total_questions} - {originalPercentage.toFixed(1)}%
                    </p>
                  </div>
                  <div className="bg-white/70 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">Resultado revisado</p>
                    <p className="text-xl font-bold text-amber-950">
                      {revisedScore}/{result.total_questions} - {revisedPercentage.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subject Info */}
        <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold text-zinc-900 mb-2">
            {result.subject_name || subjectInfo?.name}
          </h3>
          {result.created_at && (
            <p className="text-sm text-zinc-500">
              Realizado em {formatDate(result.created_at)}
            </p>
          )}
        </div>

        {/* Questions Review */}
        <h2 className="text-xl font-bold text-zinc-900 mb-4">Revisão das Questões</h2>
        
        <div className="space-y-4">
          {result.details?.map((detail, index) => (
            <div
              key={detail.question_id}
              className={`
                bg-white border rounded-xl p-4 sm:p-6
                ${detail.is_correct ? 'border-emerald-200' : 'border-red-200'}
              `}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                  ${detail.is_correct ? 'bg-emerald-100' : 'bg-red-100'}
                `}>
                  {detail.is_correct ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-500 mb-1">Questão {index + 1}</p>
                  {detail.question_annulled && (
                    <div className="mb-3 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold">Questão anulada</p>
                          <p>Esta questão foi excluída da base e o ponto foi concedido a todos os alunos.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {detail.answer_key_revised && (
                    <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold">Gabarito revisado</p>
                          <p>
                            Gabarito original: {detail.original_correct_option?.toUpperCase()}.
                            {' '}Gabarito atual: {detail.current_correct_option?.toUpperCase()}.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-zinc-900 mb-4 break-words leading-relaxed">{detail.question_text}</p>
                  
                  <div className="space-y-2">
                    {['a', 'b', 'c', 'd'].map((option) => {
                      const isSelected = detail.selected_option === option;
                      const isCorrect = detail.correct_option === option;
                      const wasOriginalCorrect = detail.answer_key_revised && detail.original_correct_option === option;
                      
                      return (
                        <div
                          key={option}
                          className={`
                            flex items-start gap-3 p-3 rounded-lg text-sm
                            ${isCorrect ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : ''}
                            ${isSelected && !isCorrect ? 'bg-red-50 text-red-800 border border-red-200' : ''}
                            ${!isSelected && !isCorrect ? 'bg-zinc-50 text-zinc-600' : ''}
                          `}
                        >
                          <span className={`
                            w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                            ${isCorrect ? 'bg-emerald-200 text-emerald-800' : ''}
                            ${isSelected && !isCorrect ? 'bg-red-200 text-red-800' : ''}
                            ${!isSelected && !isCorrect ? 'bg-zinc-200 text-zinc-600' : ''}
                          `}>
                            {option.toUpperCase()}
                          </span>
                          <span className="flex-1 min-w-0 break-words leading-relaxed">{detail.options?.[option]}</span>
                          {isCorrect && (
                            <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-1" />
                          )}
                          {wasOriginalCorrect && !isCorrect && (
                            <span className="text-xs font-medium text-amber-700 flex-shrink-0 mt-1">
                              antigo gabarito
                            </span>
                          )}
                          {isSelected && !isCorrect && (
                            <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {AI_EXPLANATIONS_ENABLED && (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAskAgent(detail)}
                        disabled={explanationLoading[detail.question_id]}
                        className="w-full sm:w-auto justify-center whitespace-normal h-auto min-h-9 text-left"
                        data-testid={`ask-agent-voar-${detail.question_id}`}
                      >
                        {explanationLoading[detail.question_id] ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2" />
                        )}
                        Pedir explicação ao Agente V.O.A.R.
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle>Agente V.O.A.R.</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                <p className="text-xs font-medium text-zinc-500 mb-1">
                  Questão
                </p>
                <p className="text-sm text-zinc-800">
                  {selectedExplanation?.questionText}
                </p>
              </div>

              {!selectedExplanation?.data ? (
                <div className="flex items-center gap-3 text-zinc-500 py-6">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Gerando explicação...</span>
                </div>
              ) : (
                <>
                  {selectedExplanation.data.status === 'review' && (
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
                      <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Possível inconsistência no gabarito</p>
                        <p className="text-sm">
                          O Agente V.O.A.R. identificou que o gabarito pode estar errado.
                          A questão será revisada pela equipe.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="prose prose-zinc max-w-none">
                    <p className="whitespace-pre-line text-zinc-800 leading-relaxed">
                      {selectedExplanation.data.explanation}
                    </p>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <Button
            onClick={() => navigate(`/quiz/${result.subject}`)}
            className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="retry-quiz"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Tentar Novamente
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex-1"
            data-testid="go-to-dashboard"
          >
            <Home className="w-4 h-4 mr-2" />
            Escolher Outra Matéria
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default ResultPage;
